import { z } from '@dcp/shared'
import type { BankAccountRow } from '@/types/banking'
import { isAccountType, type ManualAccountType } from './manualBanks.utils'

/**
 * Form schemas shared by the manual-bank dialogs: the bank form (add a bank,
 * edit a bank, add accounts to a bank) and the single-account edit dialog.
 *
 * `account_type` accepts every enum value, not only the five the pickers
 * offer: a legacy `line_of_credit` account must survive an edit that does not
 * touch its type. Which values are OFFERED is the dialogs' concern.
 */

const accountTypeSchema = z.custom<ManualAccountType>((value) => isAccountType(value), {
  message: 'Type is required',
})

/** One account or card; facility and number are mandatory, as for every manual account. */
export const manualAccountSchema = z.object({
  name: z.string().trim().min(1, 'Account/ Card Name is required'),
  account_type: accountTypeSchema,
  account_number: z.string().trim().min(1, 'Account/ Card # is required'),
  facility_id: z.string().min(1, 'Facility is required'),
})

export type ManualAccountFormValues = z.infer<typeof manualAccountSchema>

/** A bank and its accounts; `id` marks a row that already exists. */
export const manualBankSchema = z.object({
  institution_name: z.string().trim().min(1, 'Institution Name is required'),
  accounts: z
    .array(manualAccountSchema.extend({ id: z.string().optional() }))
    .min(1, 'Add at least one account or card'),
})

export type ManualBankFormValues = z.infer<typeof manualBankSchema>

/**
 * Form values of an existing account. A stored type outside the enum is left
 * empty so the form asks for one instead of silently picking a type.
 */
export function accountFormValues(account: BankAccountRow): {
  name: string
  account_type: ManualAccountType | undefined
  account_number: string
  facility_id: string
} {
  return {
    name: account.name,
    account_type: isAccountType(account.account_type) ? account.account_type : undefined,
    account_number: account.account_number ?? '',
    facility_id: account.facility_id ?? '',
  }
}

/**
 * What the bank form is doing.
 *
 * - `create`: a new manual bank (`initialName` prefills Institution Name, e.g.
 *   from the LedgerSync connect modal's "add manually" fallback);
 * - `edit`: rename the bank and add, change or remove its accounts;
 * - `add-accounts`: add accounts to an existing bank, name fixed.
 *
 * `institutionName` is the bank's EXACT stored name. `edit` and `add-accounts`
 * need a named bank: the "Unknown institution" group cannot be renamed, and new
 * accounts must carry a name.
 *
 * `defaultFacilityId` prefills Facility on the first row and on every row added
 * later — the facility of the Financial Accounts page the form opens from, or
 * null on the organization-wide page. Required, so every caller decides.
 */
export type ManualBankFormMode =
  | { kind: 'create'; initialName: string; defaultFacilityId: string | null }
  | { kind: 'edit'; institutionName: string }
  | { kind: 'add-accounts'; institutionName: string; defaultFacilityId: string | null }

/** A blank account-or-card row; Checking is the prototype's default type. */
export const BLANK_ACCOUNT_ROW = {
  name: '',
  account_type: 'checking',
  account_number: '',
  facility_id: '',
} as const
